/* eslint-disable no-lonely-if */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-unsafe-optional-chaining */
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Table.scss';

import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import { FiltersCellTemplate } from 'components/FiltersCell';
import FullScreenLoader from 'components/FullScreenLoader';
import { HeaderRowTemplate } from 'components/HeaderRow';
import { TextCellTemplate } from 'components/TextCell';
import PlanPermission from 'container/PlanPermission';
import {
  DeleteDimensionHeader,
  DeleteDimensionProperty,
  FetchDimensionGroup,
  FetchDimensionItems,
  FetchDimensionProperties,
  ReorderDimensionProperty,
  UpdateDimensionPropertyBulk,
  UpdateDimensionPropertyName,
  UpdateDimensionValues,
  UpdatePropertyHeader,
  getAddItem,
  getBaseScenario,
  getDimensionProperty,
  getFilteredDimensionItems,
  getLoaderStatus,
  getPropertiesSectionStatus,
  getSelectedDimension,
  getSettingsSectionStatus,
  handleAddItemStatus,
  handleDimensionItemsChange,
  resetBuilderDimension,
  UpdateDimensionName,
  handleDimensionLoader,
  getDimensionLoaderStatus,
  DimensionTableReordering,
} from 'redux/BuilderModeSlice';
import { hasEndDate, hasStartDate } from 'utils/GlobalHelpers';
import { AddItemProperty, getDimensionFilters, handleDimFiltersChange } from 'redux/ItemPageSlice';
import { SelectCellTemplate } from 'components/SelectCell';

import { getTableColumns, getTableRows, reorderArray } from './NewTableHelpers';
import DeleteTable from './DeleteTable';
import { getColumns, getRows } from './TableHelpersopz';

// TypeScript interfaces for better type safety
interface Focus {
  rowId: number | string;
  columnId: number | string;
}

interface DeleteModalState {
  itemId: string;
  itemName: string;
  type: string;
  status: boolean;
}

interface GridProperties {
  focusLocation?: Focus;
}

interface TableContainerStyles {
  maxH: string;
  overflow: 'auto' | 'initial';
  overflowY: 'auto' | 'initial' | 'hidden';
  overflowX: 'auto' | 'initial' | 'hidden';
}

interface StickyConfig {
  // stickyLeftColumns: number;
  stickyBottomRows: number;
  stickyTopRows: number;
  enableRowSelection: boolean;
  verticalStickyBreakpoint: number;
  horizontalStickyBreakpoint: number;
}

// Add new interfaces for performance optimization
interface ScrollState {
  scrollTop: number;
  scrollLeft: number;
  isScrolling: boolean;
}

interface ViewportState {
  visibleRows: number[];
  visibleColumns: number[];
  isDataVisible: boolean;
}

interface CellTemplates {
  header: HeaderRowTemplate;
  delete: DeleteRowCellTemplate;
  text: TextCellTemplate;
  date: DateCellTemplate;
  addRow: AddRowCellTemplate;
  dropdown: DropdownCellTemplate;
  filter: FiltersCellTemplate;
  select: SelectCellTemplate;
  [key: string]: any; // Add index signature for ReactGrid compatibility
}

// Cell interfaces
interface BaseCell {
  type: string;
  text: string;
  className?: string;
  nonEditable?: boolean;
  hasFormulaError?: boolean;
  index?: number;
  data?: any;
}

interface HeaderCell extends BaseCell {
  type: 'filter';
  options: any[];
  id: string;
  hasDelete: boolean;
  hasEdit: boolean;
  colType?: 'header';
}

interface TextCell extends BaseCell {
  type: 'text';
  placeholder?: string;
  property?: string;
  rows?: number;
  itemmId?: string;
  columnId?: string;
}

interface DateCell extends BaseCell {
  type: 'date';
  date: Date;
}

interface DropdownCell extends BaseCell {
  type: 'dropdown';
  selectOption: boolean;
  selectedValue?: any;
  values: Array<{ label: string; value: string }>;
  operation?: string;
}

interface AddRowCell extends BaseCell {
  type: 'addRow';
}

interface SelectCell extends BaseCell {
  type: 'select';
  placeholder?: string;
  property?: string;
  itemId?: string | number;
  isHeading?: boolean;
}

type Cell = HeaderCell | TextCell | DateCell | DropdownCell | AddRowCell | SelectCell;

// Row interface
interface Row {
  rowId: string | number;
  height: number;
  itemId?: string | number;
  itemName?: string;
  reorderable?: boolean;
  cells: Cell[];
}

// Column interface
interface Column {
  columnId: string;
  name?: string;
  width: number;
  resizable: boolean;
  reorderable?: boolean;
  timeDimId?: string | number;
}
interface TableData {
  id: string | number;
  name: string;
  position?: string;
  [key: string]: any;
}

// Dimension property interface
interface DimensionProperty {
  id: string | number;
  name: string;
  data_format?: string;
  items?: Array<{ name: string; value: string }>;
  [key: string]: any;
}

const swapProperties = (columns: any, target: any, columnIds: any) => {
  const targetId = Number.parseInt(target, 10);
  // Convert columnIds to numbers for comparison
  const idsToSwap = columnIds.map((id: any) => Number.parseInt(id, 10));

  const targetIndex = columns.findIndex((column: any) => column.id === targetId);

  idsToSwap.forEach((id: any) => {
    const swapIndex = columns.findIndex(
      (column: any) => Number.parseInt(column.id, 10) === Number.parseInt(id, 10),
    );
    if (targetIndex !== -1 && swapIndex !== -1) {
      const temporary = columns[targetIndex];
      columns[targetIndex] = columns[swapIndex];
      columns[swapIndex] = temporary;
    }
  });
  return columns;
};

const NewDimensionBuilderTableOpz = ({ planPermission }: any) => {
  const dispatch: any = useDispatch();
  const selectedDimension: any = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const filteredDimensionItem: any = useSelector(getFilteredDimensionItems);
  const dimProperties: any = useSelector(getDimensionProperty);
  const loaderStatus: any = useSelector(getLoaderStatus);
  const addItem: any = useSelector(getAddItem);
  const tableReference: any = useRef(null);
  const propertiesSectionStatus = useSelector(getPropertiesSectionStatus);
  const settingsSectionStatus = useSelector(getSettingsSectionStatus);
  const dimensionFilters = useSelector(getDimensionFilters);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const [dimensionProperties, setDimensionProperties] = useState<DimensionProperty[]>([]);

  useEffect(() => {
    if (dimProperties) {
      setDimensionProperties(dimProperties);
    }
  }, [dimProperties]);

  const [, setFocus] = useState<Focus>({ rowId: 'header', columnId: 'name' });
  const [tableData, setTableData] = useState<TableData[]>();
  // Use ref to track pending rows to avoid dependency issues in useEffect
  const pendingRowsReference = useRef<TableData[]>([]);
  useEffect(() => {
    const getTableData = async () => {
      if (addItem) {
        dispatch(handleAddItemStatus(false));
      }
      if (selectedDimension && selectedScenario) {
        dispatch(handleDimensionLoader(true));
        await dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        ).then((response: any) => {
          if (response?.payload?.properties) {
            response?.payload?.properties?.map((data: any) => {
              if (data?.data_format) {
                dispatch(
                  FetchDimensionGroup({
                    data,
                    parameters: { scenario_id: selectedScenario?.id },
                  }),
                );
              }
              return null;
            });
          }
        });
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(handleDimensionLoader(false));
      }
    };
    getTableData();
    setFocus({ rowId: 'header', columnId: 'name' });
  }, [selectedDimension, selectedScenario]);

  const rows: Row[] = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableData && tableData.length >= 0) {
      return selectedDimension?.name !== 'Time'
        ? getTableRows(tableData, dimensionProperties, selectedDimension)
        : getRows(tableData, dimensionProperties);
    }
    return [];
  }, [tableData, dimensionProperties, selectedDimension]);

  const memorizedColumns: Column[] = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableReference?.current?.offsetWidth) {
      return selectedDimension?.name !== 'Time'
        ? getTableColumns(
            dimensionProperties,
            tableReference?.current?.offsetWidth,
            selectedDimension,
          )
        : getColumns(dimensionProperties, tableReference?.current?.offsetWidth);
    }
    return [];
  }, [dimensionProperties, selectedDimension, tableReference?.current?.offsetWidth]);

  // Memoize the start/end date check to avoid recalculation
  const shouldEnableStartEnd = useMemo(() => {
    if (dimensionProperties?.length > 0) {
      const startDate = dimensionProperties.some((item: DimensionProperty) =>
        hasStartDate(item?.name),
      );
      const endDate = dimensionProperties.some((item: DimensionProperty) => hasEndDate(item?.name));
      return startDate || endDate;
    }
    return false;
  }, [dimensionProperties]);

  // It updates dimension properties to enable start and end date toggle
  function updateDimensionStartEnd(enable: boolean) {
    dispatch(
      UpdateDimensionName({
        item_id: selectedDimension?.id,
        request_body: {
          enable_start_end: enable ? 'true' : 'false',
        },
      }),
    );
  }

  // To check if dimensionProperties has start & end date, so enable start and end date toggle in
  useEffect(() => {
    if (shouldEnableStartEnd !== selectedDimension?.enable_start_end) {
      updateDimensionStartEnd(shouldEnableStartEnd);
    }
  }, [shouldEnableStartEnd, selectedDimension?.enable_start_end]);

  const [columns, setColumns] = useState<Column[]>(memorizedColumns);
  const [filtersData, setFiltersData]: any = useState();
  const [gridProperties, setGridProperties] = useState<GridProperties>({});
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    itemId: '',
    itemName: '',
    type: '',
    status: false,
  });

  // Add new state for performance optimization
  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollTop: 0,
    scrollLeft: 0,
    isScrolling: false,
  });

  const [viewportState, setViewportState] = useState<ViewportState>({
    visibleRows: [],
    visibleColumns: [],
    isDataVisible: false,
  });

  const scrollTimeoutReference = useRef<NodeJS.Timeout | null>(null);
  const intersectionObserverReference = useRef<IntersectionObserver | null>(null);
  const tableContainerReference = useRef<HTMLDivElement>(null);

  // Optimize column updates by combining conditions
  useEffect(() => {
    if (tableReference?.current?.offsetWidth && dimensionProperties?.length >= 0) {
      const newColumns: Column[] =
        selectedDimension?.name !== 'Time'
          ? getTableColumns(
              dimensionProperties,
              tableReference?.current?.offsetWidth,
              selectedDimension,
            )
          : getColumns(dimensionProperties, tableReference?.current?.offsetWidth);

      setColumns(newColumns);
    }
  }, [
    propertiesSectionStatus,
    settingsSectionStatus,
    dimensionProperties,
    selectedDimension,
    tableReference?.current?.offsetWidth,
  ]);

  useEffect(() => {
    if (filtersData) {
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters,
        }),
      );
    }
  }, [filtersData, dimensionFilters, dispatch]);

  useEffect(() => {
    if (filteredDimensionItem?.length >= 0) {
      // Get current tableData to preserve unsaved rows
      setTableData((currentTableData: TableData[] = []) => {
        // Get all saved item names and IDs from fetched data for matching
        const savedItemsMap = new Map<string, string | number>();
        filteredDimensionItem
          .filter((item: TableData) => item.id && item.id !== '' && item.name)
          .forEach((item: TableData) => {
            const normalizedName = item.name?.toLowerCase().trim() || '';
            if (normalizedName) {
              savedItemsMap.set(normalizedName, item.id);
            }
          });

        // Find rows that are pending (no id or empty id)
        const pendingRowsFromTable = currentTableData.filter(
          (row: TableData) => !row.id || row.id === '',
        );

        // Keep only pending rows that haven't been saved yet
        // A pending row is considered saved if:
        // 1. It has a non-empty name AND
        // 2. That name matches a saved item's name (case-insensitive)
        const remainingPendingRows = pendingRowsFromTable.filter((pendingRow: TableData) => {
          const pendingName = pendingRow.name?.toLowerCase().trim();
          // If name is empty, keep it (it's still pending)
          if (!pendingName) {
            return true;
          }
          // If name matches a saved item, remove it (it's been saved)
          return !savedItemsMap.has(pendingName);
        });

        // Merge fetched data with remaining pending rows
        // Make sure we don't have duplicates by checking IDs
        const fetchedItemIds = new Set(
          filteredDimensionItem
            .filter((item: TableData) => item.id && item.id !== '')
            .map((item: TableData) => item.id),
        );

        const mergedData = [
          ...filteredDimensionItem,
          ...remainingPendingRows.filter(
            (row: TableData) => !row.id || !fetchedItemIds.has(row.id),
          ),
        ];

        // Update pending rows ref
        pendingRowsReference.current = remainingPendingRows;

        return mergedData;
      });
    }
  }, [filteredDimensionItem]);

  // Optimize handleAddRow with useCallback
  const handleAddRow = useCallback(() => {
    const object: TableData = dimensionProperties.reduce(
      (accumulator: any, property: DimensionProperty) => {
        accumulator[property?.id] = '';
        return accumulator;
      },
      { name: '', id: '', position: '' },
    );

    // Add to both tableData and pendingRows
    setTableData((previousData: TableData[] = []) => [...previousData, object]);
    pendingRowsReference.current = [...pendingRowsReference.current, object];
    const newRowIndex = tableData?.length || 0;
    setFocus({ rowId: newRowIndex, columnId: 'name' });
    setGridProperties({ focusLocation: { rowId: newRowIndex, columnId: 'name' } });
  }, [dimensionProperties, tableData?.length]);

  useEffect(() => {
    if (addItem) handleAddRow();
  }, [addItem, handleAddRow]);

  const handleDeleteHeader = (data: any) =>
    setDeleteModal({
      itemId: data?.id || '',
      itemName: data?.text || '',
      type: 'header',
      status: true,
    });

  const handleFilterChange = (cell: any, data: any) => setFiltersData({ cell, data });

  const handleDeleteRow = async (data: any) => {
    if (addItem) {
      dispatch(handleAddItemStatus(false));
    }
    if (data?.id === '' || !data?.id) {
      // Remove from pending rows if it's an unsaved row
      const rowName = data?.name || data?.itemName;
      pendingRowsReference.current = pendingRowsReference.current.filter(
        (row: TableData) => row.name !== rowName && row !== data,
      );
      // Remove from tableData
      setTableData((previousData: TableData[] = []) =>
        previousData.filter((row: TableData) => row.name !== rowName && row !== data),
      );
      dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      setDeleteModal({
        itemId: data?.id || '',
        itemName: data?.itemName || '',
        type: 'row',
        status: true,
      });
    }
  };

  const handleDeleteModal = async (type: string) => {
    if (type === 'header') {
      setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
      dispatch(DeleteDimensionHeader({ item_id: deleteModal?.itemId })).then(() => {
        dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      });
    } else {
      if (deleteModal?.itemId) {
        setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
        await dispatch(
          DeleteDimensionProperty({
            item_id: deleteModal?.itemId,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const handleContextMenu = (
    selectedRowIds: any,
    selectedColIds: any,
    selectionMode: any,
    menuOptions: any,
    selectedRanges: any,
  ) => {
    const deleteRow = {
      id: 'deleteRow',
      label: 'Delete row',
      handler: () => {
        const filteredRows = rows?.filter((data2: Row) => {
          return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
        });
        if (filteredRows && filteredRows.length > 0) {
          handleDeleteRow({ id: filteredRows[0]?.itemId, itemName: filteredRows[0]?.itemName });
        }
      },
    };

    const deleteColumn = {
      id: 'deleteRow',
      label: 'Delete Column',
      handler: () => {
        const filteredColumn = columns?.filter((data2: Column) => {
          return data2?.columnId === selectedRanges[0][0]?.columnId && data2;
        });
        if (filteredColumn && filteredColumn.length > 0) {
          setDeleteModal({
            itemId: filteredColumn[0]?.columnId || '',
            itemName: filteredColumn[0]?.name || '',
            type: 'header',
            status: true,
          });
        }
      },
    };
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId === 'header' &&
      selectedRanges[0][0]?.columnId !== 'name'
    ) {
      return [deleteColumn];
    }
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId !== 'header' &&
      selectedRanges[0][0]?.rowId !== 'footer'
    ) {
      let selectedRows = false;
      for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
        selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
      }
      if (
        selectedRanges[0][0]?.columnId !== 'delete' &&
        selectedRows &&
        selectedDimension?.name !== 'Time'
      ) {
        menuOptions = menuOptions.filter((object: any) => object.id !== 'cut');
        return [...menuOptions, deleteRow];
      }
      return selectedRanges[0][0]?.columnId === 'delete' &&
        selectedRanges[0]?.length === 1 &&
        selectedDimension?.name !== 'Time'
        ? [deleteRow]
        : menuOptions.filter((object: any) => object.id !== 'cut');
    }
    return [];
  };

  // Memoize sticky configuration for better performance
  const stickyConfig = useMemo<StickyConfig>(
    () => ({
      // stickyLeftColumns: columns?.length <= 2 ? 0 : 2,
      stickyBottomRows: rows?.length <= 3 ? 0 : 1,
      stickyTopRows: 1,
      enableRowSelection: rows?.length > 3,
      verticalStickyBreakpoint: 100,
      horizontalStickyBreakpoint: 100,
    }),
    [columns?.length, rows?.length],
  );

  // Optimize table container styles for large datasets
  const tableContainerStyles = useMemo<TableContainerStyles>(() => {
    // Always show scrollbars when content overflows, regardless of data size
    const shouldShowVerticalScroll = rows && rows.length > 0;
    const shouldShowHorizontalScroll = columns && columns.length > 0;

    return {
      maxH: 'calc(95vh - 200px)', // Adjust based on your header height
      overflow: 'auto', // Always use auto to show scrollbars when needed
      overflowY: shouldShowVerticalScroll ? 'auto' : 'hidden',
      overflowX: shouldShowHorizontalScroll ? 'auto' : 'hidden',
    };
  }, [rows?.length, columns?.length]);

  // Memoize combined grid properties to prevent unnecessary re-renders
  const combinedGridProperties = useMemo(
    () => ({
      ...stickyConfig,
      ...gridProperties,
      disableVirtualScrolling: false, // Enable virtual scrolling for large datasets
      preventClickOnCellSelection: false,
    }),
    [stickyConfig, gridProperties],
  );

  // Memoize cell templates to prevent recreation on every render
  const cellTemplates = useMemo<CellTemplates>(
    () => ({
      header: new HeaderRowTemplate(),
      delete: new DeleteRowCellTemplate(),
      text: new TextCellTemplate(handleDeleteHeader),
      date: new DateCellTemplate(),
      addRow: new AddRowCellTemplate(handleAddRow, planPermission),
      dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
      filter: new FiltersCellTemplate(handleFilterChange),
      select: new SelectCellTemplate(),
    }),
    [handleAddRow, planPermission, handleDeleteRow, handleFilterChange, handleDeleteHeader],
  );

  useEffect(() => {
    return () => {
      setFocus({ rowId: 'header', columnId: 'name' });
      pendingRowsReference.current = []; // Clear ref on unmount
      dispatch(resetBuilderDimension());
    };
  }, []);

  const updateHeading = async (data: any) => {
    await dispatch(
      UpdatePropertyHeader({
        propertyId: data?.columnId,
        data: {
          name: data?.newCell?.text,
        },
      }),
    ).then(() => {
      dispatch(
        FetchDimensionProperties({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    });
  };

  const updateCell = async (changes: any) => {
    const change = changes[0];
    const existingRow = rows?.filter((data: any) => {
      return change?.rowId === data?.rowId;
    });
    if (change?.newCell?.colType && change?.previousCell?.colType) {
      updateHeading(change);
    } else if (
      change?.newCell?.property &&
      !change?.previousCell?.text &&
      !existingRow[0]?.itemId
    ) {
      // Update the pending row with the new name before API call
      // The row will be identified and removed from pending after API response by matching name

      await dispatch(
        AddItemProperty({
          dimension_id: Number(selectedDimension?.id),
          request_body: {
            name: change?.newCell?.text,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
      await dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      if (change?.newCell?.property) {
        await dispatch(
          UpdateDimensionPropertyName({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            name: change?.newCell?.text,
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      } else {
        await dispatch(
          UpdateDimensionValues({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            request_body: {
              property_id: change?.columnId,
              value: change?.newCell?.text,
              scenario_id: selectedScenario?.id,
            },
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const updateCellBulk = async (changes: any) => {
    const newChanges: any[] = [];
    for (const change of changes) {
      const object = {
        item_id: `${filteredDimensionItem[change?.rowId]?.id}`,
        property_id: `${change?.columnId}`,
        value: change?.newCell?.text,
      };
      newChanges.push(object);
    }
    if (newChanges?.length > 0)
      await dispatch(
        UpdateDimensionPropertyBulk({
          request_body: {
            items: newChanges,
            dimension_id: selectedDimension?.id,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
  };

  const updateCells = async (changes: any) => {
    if (addItem) dispatch(handleAddItemStatus(false));

    // Update tableData for pending rows before API call so merge logic can match them correctly
    const change = changes[0];
    if (
      change?.newCell?.property &&
      change?.rowId !== undefined &&
      typeof change.rowId === 'number'
    ) {
      // Check if this is a pending row (row without itemId)
      const existingRow = rows?.find((data: any) => data?.rowId === change?.rowId);
      if (existingRow && !existingRow?.itemId) {
        // rowId directly corresponds to tableData index (header row has rowId: 'header', not a number)
        const rowIndex = change.rowId;
        setTableData((currentTableData: TableData[] = []) => {
          if (rowIndex >= 0 && rowIndex < currentTableData.length) {
            const updatedData = [...currentTableData];
            const rowToUpdate = { ...updatedData[rowIndex] };
            // Update the name property
            if (change.columnId === 'name' || change.newCell.property) {
              rowToUpdate.name = change.newCell.text || '';
            } else if (change.columnId) {
              rowToUpdate[change.columnId] = change.newCell.text || '';
            }
            updatedData[rowIndex] = rowToUpdate;
            return updatedData;
          }
          return currentTableData;
        });
      }
    }

    await (changes?.length > 1 ? updateCellBulk(changes) : updateCell(changes));
    const dimensionFilter = JSON.parse(JSON.stringify(dimensionFilters));
    if (dimensionFilters?.length > 0) {
      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        if (dimensionFilter[index].id === changes[0].columnId) {
          const updatedValue = { name: changes[0]?.newCell?.text, status: true };
          dimensionFilter[index].data = [...dimensionFilter[index]?.data, updatedValue];
        }
      }
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters: dimensionFilter,
        }),
      );

      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        const isCheckedData = dimensionFilter?.map((data: any) => data?.data);
        const isCheckedId = dimensionFilter?.map((data: any) => data?.id);
        dispatch(
          handleDimFiltersChange({
            ischeckedItemsChecked: isCheckedData[index],
            id: isCheckedId[index],
          }),
        );
      }
    }
  };

  const handleLocationChanged = (event: any) => {
    if (Object.keys(gridProperties)?.length > 0) {
      setGridProperties({});
    }
    setFocus(event);
    return true;
  };

  const handleColumnResize = (ci: any, width: number) => {
    setColumns((column: any) => {
      const columnIndex = column.findIndex((element: any) => element.columnId === ci);
      const resizedColumn = column[columnIndex];
      const updatedColumn = { ...resizedColumn, width };
      column[columnIndex] = updatedColumn;
      return [...column];
    });
  };

  const handleCanReorderRows = (targetRowId: any) => {
    return targetRowId !== 'footer' || targetRowId !== 'header';
  };
  const canReorderColumns = (targetColumnId: any) => {
    return targetColumnId !== 'name' || targetColumnId !== 'select';
  };

  const handleColumnsReorder = async (targetColumnId: any, columnIds: any) => {
    if (dimensionProperties?.length) {
      const properties = swapProperties(
        JSON.parse(JSON.stringify(dimensionProperties)),
        targetColumnId,
        columnIds,
      );

      setDimensionProperties([...properties]);

      const reorderedColumsn = properties
        .filter((col: any) => {
          return !Number.isNaN(Number.parseInt(col.id, 10));
        })
        .map((col: any) => {
          return Number.parseInt(col.id, 10);
        });

      dispatch(
        DimensionTableReordering({
          dimId: selectedDimension?.id,
          request_body: { dim_item_prop: reorderedColumsn },
        }),
      );
    }
  };

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    if (!tableData) return;

    const to = tableData?.findIndex((person: any) => person.position - 1 === targetRowId);
    const rowsIds = rowIds.map((id) =>
      tableData?.findIndex((person: any) => person.position - 1 === id),
    );
    const reorededArray = reorderArray(tableData, rowsIds, to);
    await dispatch(
      ReorderDimensionProperty({
        selectedDimension: selectedDimension?.id,
        reorededArr: reorededArray,
        scenario_id: selectedScenario?.id,
      }),
    );
    dispatch(
      FetchDimensionItems({
        selectedId: selectedDimension?.id,
        parameters: { scenario_id: selectedScenario?.id },
      }),
    );
  };

  // Performance optimization: Handle scroll events with debouncing
  const handleScroll = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    setScrollState((previous) => ({
      ...previous,
      scrollTop: target.scrollTop,
      scrollLeft: target.scrollLeft,
      isScrolling: true,
    }));

    // Clear existing timeout
    if (scrollTimeoutReference.current) {
      clearTimeout(scrollTimeoutReference.current);
    }

    // Set scrolling to false after scroll ends
    scrollTimeoutReference.current = setTimeout(() => {
      setScrollState((previous) => ({ ...previous, isScrolling: false }));
    }, 150);
  }, []);

  // Performance optimization: Setup intersection observer for lazy loading
  const setupIntersectionObserver = useCallback(() => {
    if (!tableContainerReference.current) return;

    intersectionObserverReference.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setViewportState((previous) => ({ ...previous, isDataVisible: true }));
          } else {
            setViewportState((previous) => ({ ...previous, isDataVisible: false }));
          }
        });
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    intersectionObserverReference.current.observe(tableContainerReference.current);
  }, []);

  // Performance optimization: Handle table focus and data visibility
  const handleTableFocus = useCallback(() => {
    setViewportState((previous) => ({ ...previous, isDataVisible: true }));
  }, []);

  // Performance optimization: Force data refresh when scrolling stops
  const forceDataRefresh = useCallback(() => {
    if (viewportState.isDataVisible && !scrollState.isScrolling) {
      // Trigger a re-render of the table data
      setViewportState((previous) => ({ ...previous, isDataVisible: true }));
    }
  }, [viewportState.isDataVisible, scrollState.isScrolling]);

  // Performance optimization: Setup scroll event listeners and intersection observer
  useEffect(() => {
    if (tableContainerReference.current) {
      setupIntersectionObserver();

      // Add scroll event listener
      const container = tableContainerReference.current;
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (intersectionObserverReference.current) {
          intersectionObserverReference.current.disconnect();
        }
        if (scrollTimeoutReference.current) {
          clearTimeout(scrollTimeoutReference.current);
        }
      };
    }
    return () => {
      if (tableContainerReference.current) {
        tableContainerReference.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [setupIntersectionObserver, handleScroll]);

  // Performance optimization: Force data refresh when scrolling stops
  useEffect(() => {
    if (!scrollState.isScrolling && viewportState.isDataVisible) {
      forceDataRefresh();
    }
  }, [scrollState.isScrolling, viewportState.isDataVisible, forceDataRefresh]);

  return (
    <Flex
      height={'100%'}
      width={'100%'}
      flexDirection={'column'}
      ref={tableReference}
      flex={1}
      overflow={'hidden'}
      position={'relative'}
    >
      <Flex justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
        <Box>
          <Text fontSize={'md'} width={'fit-content'} mb={'.4rem'}>
            Items
          </Text>
        </Box>
        {columns?.length && rows?.length && !(dimensionLoader || loaderStatus) && <DeleteTable />}
      </Flex>
      {dimensionLoader || loaderStatus ? (
        <FullScreenLoader height='inherit' bgColor='transparent' />
      ) : (
        <>
          {columns?.length && rows?.length ? (
            <>
              <Box
                className='md-table-container sm-scroll'
                width='100%'
                maxH={tableContainerStyles.maxH}
                overflow={tableContainerStyles.overflow as 'auto' | 'initial'}
                overflowY={tableContainerStyles.overflowY}
                overflowX={tableContainerStyles.overflowX}
                mt={4}
                ref={tableContainerReference}
                onClick={forceDataRefresh}
                onFocus={handleTableFocus}
              >
                <ReactGrid
                  rows={rows}
                  columns={columns}
                  enableFillHandle
                  enableRangeSelection
                  enableColumnSelection={true}
                  {...combinedGridProperties}
                  onRowsReordered={handleRowsReorder}
                  onFocusLocationChanging={handleLocationChanged}
                  onCellsChanged={planPermission ? undefined : updateCells}
                  onContextMenu={planPermission ? undefined : handleContextMenu}
                  onColumnResized={planPermission ? undefined : handleColumnResize}
                  canReorderRows={handleCanReorderRows}
                  canReorderColumns={canReorderColumns}
                  onColumnsReordered={handleColumnsReorder}
                  customCellTemplates={cellTemplates}
                />
              </Box>
            </>
          ) : (
            'NA'
          )}
        </>
      )}
      {deleteModal.status && (
        <DeleteGroupModal
          isOpen={deleteModal.status}
          onClose={() => setDeleteModal({ itemId: '', itemName: '', type: '', status: false })}
          itemName={deleteModal.itemName || 'some name'}
          onDelete={() => handleDeleteModal(deleteModal.type)}
          orgChart={true}
        />
      )}
    </Flex>
  );
};

export default PlanPermission(NewDimensionBuilderTableOpz);
